import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumberForDimensions, getDimensionUnit, getGaugeMap, Literal, SearchFormData } from '@steelbuy/ts-shared';
import { SearchSummaryHeader } from '@steelbuy/ui-primitive';

import './SearchListingsSearchSummary.scss';

export type SearchListingsSearchSummaryProps = {
    searchFormData: SearchFormData;
    children: ReactNode;
};

const MATERIAL_PROPERTIES: (keyof SearchFormData)[] = ['definition', 'materialType', 'grade', 'product'];

export const SearchListingsSearchSummary = ({ children, searchFormData }: SearchListingsSearchSummaryProps) => {
    const { t } = useTranslation('domainModel');

    const dimensionUnitKey = getDimensionUnit();

    const materialProperties = useMemo(
        () =>
            MATERIAL_PROPERTIES.reduce<Partial<Record<keyof SearchFormData, string>>>((acc, property) => {
                const value = searchFormData[property];
                if (value) {
                    acc[property] = t(`material.${property}.value.${value}` as Literal);
                }
                return acc;
            }, {} as const),
        [searchFormData]
    );

    const gauges = useMemo(() => {
        const gaugeMap = getGaugeMap(searchFormData) ?? [];

        return gaugeMap.map((value) => {
            const thicknessRange = `${t(`material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                value: value.min,
            })} - ${t(`material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                value: value.max,
            })}`;
            return {
                label: value.gauge ? `${value.gauge} (${thicknessRange})` : thicknessRange,
                value: value.min,
            };
        });
    }, [searchFormData]);

    const gaugeLabel = (val?: number) => gauges.find((opt) => opt.value === val)?.label ?? null;

    const formattedDimensions = useMemo(() => {
        const dimensionUnitLabel = t(`material.dimensions.units.${dimensionUnitKey}.label`);

        return [searchFormData.thickness, searchFormData.width, searchFormData.length]
            .filter(Boolean)
            .map((val) => `${formatNumberForDimensions(val)}${dimensionUnitLabel}`);
    }, [searchFormData]);

    const dimensionText = useMemo(() => {
        const properties: string[] = [];

        if (gauges.length && searchFormData.gauge) {
            const label = gaugeLabel(searchFormData.gauge);
            if (label) properties.push(label);
        }

        properties.push(...formattedDimensions);

        return properties.join(' x ');
    }, [gauges, searchFormData.gauge, formattedDimensions]);

    const firstLine = materialProperties.definition ?? materialProperties.materialType ?? '';

    const secondLine = [dimensionText, materialProperties.grade, materialProperties.product].filter(Boolean).join(' ');

    return (
        <div className="search-listings-search-summary">
            <div className="search-summary">
                <div className="search-summary__header-properties-wrapper">
                    <SearchSummaryHeader
                        symbol={searchFormData.product?.toLowerCase()}
                        firstLine={firstLine}
                        secondLine={secondLine}
                        thirdLine=""
                    />
                </div>
                <div className="search-summary__children-wrapper">{children}</div>
            </div>
        </div>
    );
};
